import { Deck, DeckCard } from "~/types/room/types";

// Deck IDs for the "Other Artists" feature - only paid non-oracle decks
export const OTHER_ARTISTS_DECK_IDS = [
  "49eb31ea-e2e6-49e1-965c-63a5bb9d0579", // Mushroom Tarot
  "adacec7b-820b-4231-b9e4-caf4b8d97a46", // Gothmancy
  "d286121d-5a70-40a0-ab80-ebe5fe1ea41a", // Solar Kingdom
  "855367d8-2e82-4b62-8090-22c08d4c836b", // Black Deck
  "c23a5d51-0a30-43f4-a3fb-695432fcd323", // Eldritch Overload
  "f9fc34e1-7103-45bf-8d29-3cf989d47f8a", // Mixed Signals
];

// Mapping of alternate names used by different decks
const COURT_CARD_ALIASES: Record<string, string[]> = {
  page: ["learner", "daughter"],
  knight: ["fighter", "son"],
  queen: ["maker", "mother"],
  king: ["speaker", "father"],
};

const SUIT_ALIASES: Record<string, string[]> = {
  pentacles: ["stones", "coins"],
  cups: ["potions"],
  swords: ["words"],
  wands: [], // wands is consistent across decks
};

// Major arcana that might have different names
const MAJOR_ARCANA_ALIASES: Record<string, string[]> = {
  "high priestess": ["seer"],
  "wheel of fortune": ["wheel"],
};

/**
 * Normalizes a card name by extracting the canonical parts
 * Returns an object with the normalized components for matching
 */
const normalizeCardName = (name: string): { rank?: string; suit?: string; major?: string } => {
  const lower = name.toLowerCase();
  
  // Check for court cards (Page/Knight/Queen/King of X)
  for (const [canonical, aliases] of Object.entries(COURT_CARD_ALIASES)) {
    const allRanks = [canonical, ...aliases];
    for (const rank of allRanks) {
      if (lower.includes(rank)) {
        // Find the suit
        for (const [canonicalSuit, suitAliases] of Object.entries(SUIT_ALIASES)) {
          const allSuits = [canonicalSuit, ...suitAliases];
          for (const suit of allSuits) {
            if (lower.includes(suit)) {
              return { rank: canonical, suit: canonicalSuit };
            }
          }
        }
      }
    }
  }
  
  // Check for numbered cards (Ace/Two/Three... of X)
  const numberMatch = lower.match(/\b(ace|one|two|three|four|five|six|seven|eight|nine|ten)\b/);
  if (numberMatch) {
    for (const [canonicalSuit, suitAliases] of Object.entries(SUIT_ALIASES)) {
      const allSuits = [canonicalSuit, ...suitAliases];
      for (const suit of allSuits) {
        if (lower.includes(suit)) {
          return { rank: numberMatch[1], suit: canonicalSuit };
        }
      }
    }
  }
  
  // Check for major arcana
  // Remove common prefixes like "the" and any characters after em-dash
  const cleanName = lower
    .replace(/^the\s+/, "")
    .replace(/\s*[—–-]\s*.+$/, "") // Remove em-dash and anything after (like Japanese)
    .trim();
  
  // Check for aliased major arcana
  for (const [canonical, aliases] of Object.entries(MAJOR_ARCANA_ALIASES)) {
    if (cleanName === canonical || aliases.includes(cleanName)) {
      return { major: canonical };
    }
  }
  
  // Return the cleaned name as major arcana
  return { major: cleanName };
};

/**
 * Checks if two cards are the same card (same meaning, different deck artwork)
 */
export const cardsMatch = (cardName1: string, cardName2: string): boolean => {
  const norm1 = normalizeCardName(cardName1);
  const norm2 = normalizeCardName(cardName2);
  
  // Both are minor/court cards
  if (norm1.rank && norm2.rank && norm1.suit && norm2.suit) {
    return norm1.rank === norm2.rank && norm1.suit === norm2.suit;
  }
  
  // Both are major arcana
  if (norm1.major && norm2.major) {
    return norm1.major === norm2.major;
  }
  
  return false;
};

export interface MatchingCard {
  card: DeckCard;
  deck: Deck;
  imagePath: string;
}

/**
 * Finds matching cards from other decks for a given card name
 * Returns cards from up to `count` random decks
 */
export const findMatchingCardsFromOtherDecks = (
  cardName: string,
  allDecks: Deck[],
  count: number = 3
): MatchingCard[] => {
  // Filter to only the allowed decks
  const eligibleDecks = allDecks.filter((deck) => 
    OTHER_ARTISTS_DECK_IDS.includes(deck.id)
  );
  
  // Find matching cards from each deck
  const matches: MatchingCard[] = [];
  
  for (const deck of eligibleDecks) {
    const matchingCard = deck.cards.find((card) => cardsMatch(cardName, card.name));
    if (matchingCard) {
      matches.push({
        card: matchingCard,
        deck,
        imagePath: `${deck.basePath}/${matchingCard.artwork}`,
      });
    }
  }
  
  // Shuffle and return up to `count` matches
  const shuffled = matches.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

